<template>
  <component
    :is="tag"
    :style="containerStyle"
  >
    <span
      ref="content"
      :style="lineClampStyle"
    >
      <slot />
    </span>

    <slot
      name="after"
      :clamped="clamped"
      :expanded="isExpanded"
      :toggle="toggle"
    />
  </component>
</template>



<script>
  // Replacement for the vue-clamp package (v0.4.1), which has no Vue 3 compatible version
  // and is unmaintained. Implements the subset of the original API that this codebase uses:
  // the `tag` and `max-lines` props, the `autoresize` prop, and the `#after` scoped slot
  // exposing `{ clamped, expanded, toggle }` for "Read More" / "Read Less" controls.
  export default {
    name: 'VClamp',

    props: {
      tag: {
        type: String,
        default: 'span'
      },

      maxLines: {
        type: Number,
        default: null
      },

      autoresize: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        contentOverflows: false,
        isExpanded: false
      };
    },

    computed: {
      // True only when the content is currently being truncated. False once expanded,
      // so call sites using `clamped ? 'Read More' : 'Read Less'` flip to "Read Less".
      clamped() {
        return this.contentOverflows && !this.isExpanded;
      },

      // When expanded, push the natural bottom of the wrapper down so a trailing toggle
      // button rendered via the `#after` slot clears any sticky footer overlapping the
      // scroll container (e.g. cf-modal__footer--sticky).
      containerStyle() {
        return this.isExpanded ? { paddingBottom: '80px' } : {};
      },

      lineClampStyle() {
        if (!this.maxLines || this.isExpanded) return {};

        return {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          '-webkit-line-clamp': this.maxLines,
          overflow: 'hidden'
        };
      }
    },

    mounted() {
      this.measure();

      if (this.autoresize && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.$refs.content);
      }

      // Web fonts can swap in after first paint and shift the line count.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.measure());
      }
    },

    updated() {
      this.$nextTick(this.measure);
    },

    beforeUnmount() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },

    methods: {
      measure() {
        const el = this.$refs.content;

        if (!el) return;

        // When expanded the clamp CSS isn't applied, so scrollHeight === clientHeight
        // would falsely zero out overflow. Preserve the prior value until the user
        // collapses again.
        if (this.isExpanded) return;

        // The `> 1` tolerance avoids sub-pixel false positives from line-height rounding.
        this.contentOverflows = el.scrollHeight - el.clientHeight > 1;
      },

      toggle() {
        this.isExpanded = !this.isExpanded;

        this.$nextTick(this.measure);
      }
    }
  };
</script>
